import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DomainBiomassDay } from '@/domain/cages/overview.types';
import { findLastPositiveGrowthDay, OverviewChartCard } from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { localizedDateFormat } from '@/utils/date';
import { formatUniversalUnitByLocale, parseLocale } from '@/utils/localeFormatters';

import { useGrowthChartSpec } from './useGrowthChartSpec';

import styles from './CageWeightGrowthView.module.scss';

const CHART_HEIGHT = 400;
const INFO_COLUMN_WIDTH = 375;
const GROWTH_DECIMALS = 2;
const SKELETON_WIDTH = 100;
const SKELETON_HEIGHT = 24;
const SKELETON_RADIUS = 8;

export interface CageWeightGrowthViewProps {
    days: DomainBiomassDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    weightUnitTag: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (dates: DateRange) => void;
}

export default function CageWeightGrowthView(props: CageWeightGrowthViewProps) {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        weightUnitTag,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
    } = props;
    const { t: tCharts } = useTranslation('cageCharts');
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const spec = useGrowthChartSpec({
        days,
        graphedDaypartTags,
        activeDaypartTags,
        weightUnitTag,
    });

    const lastPositiveGrowthDay = findLastPositiveGrowthDay(days);
    const lastGrowthValue = lastPositiveGrowthDay?.data.weightGrowthAvg ?? null;

    function renderMeasurement(label: string, value: string) {
        return (
            <div className={styles.measurementRow}>
                <Caption>{label}</Caption>
                {isLoading ? (
                    <SkeletonBox
                        width={SKELETON_WIDTH}
                        height={SKELETON_HEIGHT}
                        borderRadius={SKELETON_RADIUS}
                    />
                ) : (
                    <span className={styles.measurementValue}>{value}</span>
                )}
            </div>
        );
    }

    return (
        <GridLayout contentLayout="1-fixed" secondColumnWidth={INFO_COLUMN_WIDTH}>
            <OverviewChartCard
                title={tCharts('title.growth')}
                chartName="cage-growth"
                spec={spec}
                chartHeight={CHART_HEIGHT}
                isLoading={isLoading}
                error={error}
                {...retry}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
            />
            <ContentBlock
                title={t('common.labels.information')}
                titleIcon={<TitleIcon icon={Info} />}>
                <div className={styles.infoText}>
                    {t('cageWeight.growth.lastPositiveMeasurementTooltip')}
                </div>
                {renderMeasurement(
                    t('common.labels.lastMeasurementDay'),
                    lastPositiveGrowthDay
                        ? localizedDateFormat({
                              dateString: lastPositiveGrowthDay.day,
                              onlydate: true,
                              locale: i18n.language,
                          })
                        : t('common.labels.noData')
                )}
                {renderMeasurement(
                    t('common.labels.lastMeasurement'),
                    lastGrowthValue != null
                        ? formatUniversalUnitByLocale(
                              lastGrowthValue,
                              weightUnitTag,
                              locale,
                              GROWTH_DECIMALS
                          )
                        : t('common.labels.noData')
                )}
            </ContentBlock>
        </GridLayout>
    );
}
